import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import {
    useReactTable,
    getCoreRowModel,
    getSortedRowModel,
    getFilteredRowModel,
    flexRender,
} from '@tanstack/react-table';
import { DateRange } from 'react-date-range';
import { addDays } from 'date-fns';
import { Modal, Button } from 'react-bootstrap';

// Import all necessary styles for react-date-range
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import Sidedash from '../uicomponents/Sidedash';

const Allattendance = () => {
    const [attendanceData, setAttendanceData] = useState([]);
    const [dateRange, setDateRange] = useState([
        {
            startDate: new Date(),
            endDate: addDays(new Date(), 7),
            key: 'selection'
        }
    ]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [sorting, setSorting] = useState([]);
    const [globalFilter, setGlobalFilter] = useState('');
    const [showCalendar, setShowCalendar] = useState(false);
    const [showStudentModal, setShowStudentModal] = useState(false);
    const [selectedStudent, setSelectedStudent] = useState(null);
    // Use direct backend URL instead of proxy
    const host = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';
    const calendarRef = useRef(null);

    const handleShowStudentDetails = useCallback(async (student) => {
        try {
            const response = await fetch(`${host}/students/show/${student._id}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'auth-token': localStorage.getItem('token'),
                }
            });

            console.log(response);
            if (!response.ok) {
                throw new Error('Failed to fetch student details');
            }

            const data = await response.json();
            setSelectedStudent(data);
            setShowStudentModal(true);
        } catch (error) {
            console.error('Error fetching student details:', error);
        }
    }, [host]);

    const columns = useMemo(() => [
        {
            header: 'Student Name',
            accessorKey: 'studentId',
            cell: ({ row }) => {
                const student = row.original.studentId;
                return student ? (
                    <span
                        className="text-primary"
                        style={{ cursor: 'pointer' }}
                        onClick={() => handleShowStudentDetails(student)}
                    >
                        {student.name}
                    </span>
                ) : 'N/A';
            },
        },
        {
            header: 'Date',
            accessorKey: 'date',
            cell: ({ row }) => new Date(row.original.date)
                .toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' }),
        },
        {
            header: 'Check In',
            accessorKey: 'checkInTimes',
            cell: ({ row }) => row.original.checkInTimes
                .map(entry => new Date(entry.time)
                    .toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour12: true }))
                .join(', '),
        },
        {
            header: 'Check Out',
            accessorKey: 'checkOutTimes',
            cell: ({ row }) => row.original.checkOutTimes
                .map(entry => entry.time ?
                    new Date(entry.time)
                        .toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour12: true })
                    : 'Not checked out')
                .join(', '),
        },
        {
            header: 'Status',
            accessorKey: 'status',
            cell: ({ row }) => {
                const checkInCount = row.original.checkInTimes.length;
                const checkOutCount = row.original.checkOutTimes.length;
                return checkOutCount >= checkInCount ? 'Checked Out' : 'Available';
            },
        }
    ], [handleShowStudentDetails]);

    const table = useReactTable({
        data: attendanceData,
        columns,
        state: {
            sorting,
            globalFilter,
        },
        onSortingChange: setSorting,
        onGlobalFilterChange: setGlobalFilter,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
    });

    const fetchAttendanceData = async () => {
        // Hide calendar popup when fetching
        setShowCalendar(false);
        try {
            setLoading(true);
            setError(null);
            // Build query parameters for date range
            const params = new URLSearchParams({
                startDate: dateRange[0].startDate.toISOString(),
                endDate: dateRange[0].endDate.toISOString(),
            });
            const res = await fetch(`${host}/attendance/range?${params}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'auth-token': localStorage.getItem('token'),
                },
            });
            if (!res.ok) {
                throw new Error(`HTTP error! status: ${res.status}`);
            }
            const data = await res.json();
            setAttendanceData(data);
        } catch (error) {
            console.error('Error fetching attendance data:', error);
            setError(`Failed to fetch attendance data: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    // Fetch the latest attendance entries on mount (default 10)
    const fetchLatestAttendanceData = async () => {
        try {
            setLoading(true);
            setError(null);
            const res = await fetch(`${host}/attendance/latest?limit=10`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'auth-token': localStorage.getItem('token'),
                },
            });
            if (!res.ok) {
                throw new Error(`HTTP error! status: ${res.status}`);
            }
            const data = await res.json();
            setAttendanceData(data);
        } catch (error) {
            console.error('Error fetching latest attendance data:', error);
            setError(`Failed to fetch latest attendance data: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    // On component mount, load latest entries
    useEffect(() => {
        fetchLatestAttendanceData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (calendarRef.current && !calendarRef.current.contains(event.target)) {
                setShowCalendar(false);
            }
        };

        if (showCalendar) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showCalendar]);

    const handleCloseStudentModal = () => {
        setSelectedStudent(null);
        setShowStudentModal(false);
    };

    return (
        <div className="container-fluid">
            <div className="row">
                <Sidedash />
                <div className="col-md-9 pt-3">
                    <h2 className="text-center mb-4">Attendance Records</h2>

                    {/* Date range picker and fetch button */}
                    <div className="row mb-4">
                        <div className="col-md-12 d-flex align-items-center">
                            <div className="position-relative me-2" ref={calendarRef}>
                                <div className="input-group" style={{ height: '38px' }}>
                                    <span
                                        className="input-group-text bg-white border-end-0"
                                        style={{ cursor: 'pointer', height: '100%', display: 'flex', alignItems: 'center' }}
                                        onClick={() => setShowCalendar(true)}
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" class="bi bi-calendar2-event" viewBox="0 0 16 16">
                                            <path d="M11 7.5a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5z" />
                                            <path d="M3.5 0a.5.5 0 0 1 .5.5V1h8V.5a.5.5 0 0 1 1 0V1h1a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V3a2 2 0 0 1 2-2h1V.5a.5.5 0 0 1 .5-.5M2 2a1 1 0 0 0-1 1v11a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V3a1 1 0 0 0-1-1z" />
                                            <path d="M2.5 4a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5H3a.5.5 0 0 1-.5-.5z" />
                                        </svg>
                                    </span>
                                    <input
                                        type="text"
                                        readOnly
                                        className="form-control rounded-end"
                                        style={{ cursor: 'pointer', height: '100%' }}
                                        onClick={() => setShowCalendar(true)}
                                        value={`${dateRange[0].startDate.toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' })} - ${dateRange[0].endDate.toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' })}`}
                                    />
                                </div>
                                {showCalendar && (
                                    <div className="position-absolute bg-white border p-2 mt-1" style={{ zIndex: 1000 }}>
                                        <DateRange
                                            onChange={item => {
                                                setDateRange([item.selection]);
                                            }}
                                            moveRangeOnFirstSelection={false}
                                            ranges={dateRange}
                                            rangeColors={["#3d91ff"]}
                                        />
                                    </div>
                                )}
                            </div>
                            <button
                                className="btn btn-primary mb-0"
                                onClick={fetchAttendanceData}
                                disabled={loading}
                            >
                                {loading ? 'Loading...' : 'Get Attendance Records'}
                            </button>
                        </div>
                    </div>

                    {error && (
                        <div className="alert alert-danger" role="alert">
                            {error}
                        </div>
                    )}

                    <div className="table-responsive">
                        <table className="table table-striped table-bordered">
                            <thead>
                                {table.getHeaderGroups().map(headerGroup => (
                                    <tr key={headerGroup.id}>
                                        {headerGroup.headers.map(header => (
                                            <th
                                                key={header.id}
                                                onClick={header.column.getToggleSortingHandler()}
                                                style={{ cursor: 'pointer' }}
                                            >
                                                {flexRender(
                                                    header.column.columnDef.header,
                                                    header.getContext()
                                                )}
                                                <span>
                                                    {header.column.getIsSorted() === 'asc' ? ' 🔼' :
                                                        header.column.getIsSorted() === 'desc' ? ' 🔽' : ''}
                                                </span>
                                            </th>
                                        ))}
                                    </tr>
                                ))}
                            </thead>
                            <tbody>
                                {table.getRowModel().rows.map(row => (
                                    <tr key={row.id}>
                                        {row.getVisibleCells().map(cell => (
                                            <td key={cell.id}>
                                                {flexRender(
                                                    cell.column.columnDef.cell,
                                                    cell.getContext()
                                                )}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Student Details Modal */}
                    <Modal show={showStudentModal} onHide={handleCloseStudentModal}>
                        <Modal.Header closeButton>
                            <Modal.Title>Student Details</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            {selectedStudent && (
                                <div className="student-details">
                                    <div className="mb-4">
                                        <h4 className="text-primary mb-3">{selectedStudent.name}</h4>
                                        <div className="row">
                                            <div className="col-md-6">
                                                <div className="detail-item mb-3">
                                                    <label className="text-muted mb-1">Email</label>
                                                    <p className="mb-0">{selectedStudent.email}</p>
                                                </div>
                                                <div className="detail-item mb-3">
                                                    <label className="text-muted mb-1">Phone</label>
                                                    <p className="mb-0">{selectedStudent.phone || 'Not provided'}</p>
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <div className="detail-item mb-3">
                                                    <label className="text-muted mb-1">UID</label>
                                                    <p className="mb-0">{selectedStudent.uid || 'Not provided'}</p>
                                                </div>
                                                <div className="detail-item mb-3">
                                                    <label className="text-muted mb-1">Attendance Code</label>
                                                    <p className="mb-0">{selectedStudent.attendanceCode || 'Not provided'}</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="secondary" onClick={handleCloseStudentModal}>
                                Close
                            </Button>
                        </Modal.Footer>
                    </Modal>
                </div>
            </div>
        </div>
    );
};

export default Allattendance;
